// /platform/enquiries — what people send from the orio360.com form (website
// plan D3, §6.4).
//
// The team follows each enquiry up (New → Contacted → Converted or Closed, with
// notes) and turns one into a business with Create business: that opens
// /tenants/new filled in through router state (never the query string, so no
// personal data goes in the URL), and the new-business page marks the enquiry
// Converted once the business is created.
//
// MODERATOR and above (route gate 60); the controller enforces the same.

import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Drawer,
  ErrorBoundary,
  Input,
  PageContainer,
  Select,
  Skeleton,
  TextArea,
  useToast,
} from '@bbpos/ui';
import { enquiriesService } from '@/services/enquiries.service';
import { subscriptionService } from '@/services/subscription.service';
import { cn } from '@/constants/design-tokens';
import type { TenantCreatePrefill, TenantCreateRouterState } from '@/pages/TenantCreatePage';
import type { SubscriptionPlan } from '@/types/subscription';
import {
  ENQUIRY_BUSINESS_TYPES,
  ENQUIRY_INTERESTS,
  ENQUIRY_STATUSES,
  type Enquiry,
  type EnquiryBusinessType,
  type EnquiryFilters,
  type EnquiryInterest,
  type EnquiryNotifyStatus,
  type EnquiryStatus,
  type UpdateEnquiryInput,
} from '@/types/enquiry';

const LIST_KEY = ['platform-enquiries'];
const PAGE_SIZE = 20;

const STATUS_LABELS: Record<EnquiryStatus, string> = {
  NEW: 'New',
  CONTACTED: 'Contacted',
  CONVERTED: 'Converted',
  CLOSED: 'Closed',
};

/** "Reason" is the `interest` column. */
const REASON_LABELS: Record<EnquiryInterest, string> = {
  DEMO: 'Book a demo',
  TRIAL: 'Free trial',
  PRICING: 'Prices',
  SUPPORT: 'Help',
  OTHER: 'Something else',
};

const BUSINESS_TYPE_LABELS: Record<EnquiryBusinessType, string> = {
  RETAIL: 'Shop',
  RESTAURANT: 'Restaurant',
  OTHER: 'Other',
};

/** "Email to team" is the `notify_status` column. */
const NOTIFY_LABELS: Record<EnquiryNotifyStatus, string> = {
  SENT: 'Sent',
  FAILED: 'Not sent',
  SKIPPED: 'Off',
};

const STATUS_PILL: Record<EnquiryStatus, string> = {
  NEW: 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300',
  CONTACTED: 'bg-amber-50 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  CONVERTED: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  CLOSED: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300',
};

const STATUS_DOT: Record<EnquiryStatus, string> = {
  NEW: 'bg-brand-500',
  CONTACTED: 'bg-amber-500',
  CONVERTED: 'bg-emerald-500',
  CLOSED: 'bg-neutral-400',
};

const NOTIFY_PILL: Record<EnquiryNotifyStatus, string> = {
  SENT: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  FAILED: 'bg-rose-50 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
  SKIPPED: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300',
};

const KPI_CARDS: Array<{
  status: EnquiryStatus;
  gradient: string;
  tint: string;
  /** Icon path (24×24, stroked). */
  icon: string;
  hint?: string;
}> = [
  {
    status: 'NEW',
    gradient: 'from-brand-500 to-indigo-500',
    tint: 'bg-brand-50/60 dark:bg-brand-900/20',
    icon: 'M3 8l9 6 9-6M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  },
  {
    status: 'CONTACTED',
    gradient: 'from-amber-500 to-orange-500',
    tint: 'bg-amber-50/60 dark:bg-amber-900/20',
    icon: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.95.68l1.5 4.49a1 1 0 01-.5 1.21l-2.26 1.13a11.04 11.04 0 005.52 5.52l1.13-2.26a1 1 0 011.21-.5l4.49 1.5a1 1 0 01.68.95V19a2 2 0 01-2 2h-1C9.72 21 3 14.28 3 6V5z',
    hint: 'We talked to them',
  },
  {
    status: 'CONVERTED',
    gradient: 'from-teal-500 to-emerald-500',
    tint: 'bg-emerald-50/60 dark:bg-emerald-900/20',
    icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    hint: 'They have a business now',
  },
  {
    status: 'CLOSED',
    gradient: 'from-neutral-400 to-neutral-600',
    tint: 'bg-neutral-50 dark:bg-neutral-800/60',
    icon: 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z',
    hint: 'No more follow-up',
  },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  ...ENQUIRY_STATUSES.map((s) => ({ value: s, label: STATUS_LABELS[s] })),
];
const REASON_OPTIONS = [
  { value: '', label: 'All reasons' },
  ...ENQUIRY_INTERESTS.map((i) => ({ value: i, label: REASON_LABELS[i] })),
];
const BUSINESS_TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  ...ENQUIRY_BUSINESS_TYPES.map((t) => ({ value: t, label: BUSINESS_TYPE_LABELS[t] })),
];

const firstValue = (v: string | string[]) => (Array.isArray(v) ? v[0] : v) || undefined;

function formatDay(value: string): string {
  return new Date(value).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatTime(value: string): string {
  return new Date(value).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

function formatWhen(value: string | null): string | null {
  return value
    ? new Date(value).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })
    : null;
}

/**
 * The new-business form's starting values. Business type only for RETAIL /
 * RESTAURANT; the plan only when it matches a plan in the catalog (by name or
 * display name, any case) — the form needs the plan's `name`.
 */
function toPrefill(enquiry: Enquiry, plans: SubscriptionPlan[]): TenantCreatePrefill {
  const wanted = enquiry.plan_name?.trim().toLowerCase();
  const plan = wanted
    ? plans.find((p) => p.name.toLowerCase() === wanted || p.display_name.toLowerCase() === wanted)
    : undefined;
  const type = enquiry.business_type;
  return {
    businessName: enquiry.business_name ?? '',
    businessType: type === 'RETAIL' || type === 'RESTAURANT' ? type : undefined,
    ownerName: enquiry.name,
    ownerEmail: enquiry.contact_email,
    ownerPhone: enquiry.phone,
    city: enquiry.city ?? '',
    plan: plan?.name,
  };
}

function StatusPill({ status }: { status: EnquiryStatus }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold',
        STATUS_PILL[status],
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', STATUS_DOT[status])} aria-hidden="true" />
      {STATUS_LABELS[status]}
    </span>
  );
}

function NotifyPill({ status }: { status: EnquiryNotifyStatus | null }) {
  if (!status) return <span className="text-xs text-neutral-400 dark:text-neutral-500">—</span>;
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
        NOTIFY_PILL[status],
      )}
    >
      {NOTIFY_LABELS[status]}
    </span>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium text-neutral-500 dark:text-neutral-400">{label}</dt>
      <dd className="mt-0.5 break-words text-sm text-neutral-900 dark:text-neutral-100">
        {children ?? <span className="text-neutral-400 dark:text-neutral-500">Not given</span>}
      </dd>
    </div>
  );
}

const CONTACT_LINK =
  'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:shadow-lg';

// ─── Side panel ─────────────────────────────────────────────────────────────

function EnquiryPanel({
  enquiry,
  saving,
  onUpdate,
  onCreateBusiness,
  onOpenBusiness,
}: {
  enquiry: Enquiry;
  saving: boolean;
  onUpdate: (input: UpdateEnquiryInput) => void;
  onCreateBusiness: () => void;
  onOpenBusiness: (tenantId: string) => void;
}) {
  // Keyed by enquiry id, so the box starts from that enquiry's saved notes.
  const [notes, setNotes] = useState(enquiry.notes ?? '');

  return (
    <div className="space-y-6 p-4 animate-fade-in">
      <div className="flex flex-wrap gap-2">
        <a href={`tel:${enquiry.phone}`} className={cn(CONTACT_LINK, 'bg-gradient-to-r from-brand-600 to-indigo-600')}>
          Call
        </a>
        <a
          href={`mailto:${enquiry.contact_email}`}
          className={cn(CONTACT_LINK, 'bg-gradient-to-r from-violet-600 to-pink-600')}
        >
          Email
        </a>
        <a
          href={`https://wa.me/${enquiry.phone.replace(/\D/g, '')}`}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(CONTACT_LINK, 'bg-gradient-to-r from-teal-600 to-emerald-600')}
        >
          WhatsApp
        </a>
      </div>

      <section>
        <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400 dark:text-neutral-500">
          Status
        </h3>
        <div className="grid grid-cols-2 gap-2" role="group" aria-label="Status">
          {ENQUIRY_STATUSES.map((status) => {
            const current = enquiry.status === status;
            return (
              <button
                key={status}
                type="button"
                aria-pressed={current}
                disabled={current || saving}
                onClick={() => onUpdate({ status })}
                className={cn(
                  'flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-semibold transition-all duration-200',
                  current
                    ? cn(STATUS_PILL[status], 'border-transparent ring-2 ring-offset-1 ring-brand-300 dark:ring-brand-700 dark:ring-offset-neutral-900')
                    : 'border-neutral-200 text-neutral-700 hover:bg-brand-50 hover:border-brand-200 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-brand-900/20',
                  saving && !current && 'opacity-60',
                )}
              >
                <span className={cn('h-2 w-2 rounded-full', STATUS_DOT[status])} aria-hidden="true" />
                {STATUS_LABELS[status]}
              </button>
            );
          })}
        </div>
      </section>

      <section>
        <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400 dark:text-neutral-500">
          Notes
        </h3>
        <TextArea
          aria-label="Notes"
          value={notes}
          onChange={setNotes}
          rows={4}
          maxLength={4000}
          placeholder="Write what you talked about, and what to do next."
        />
        <div className="mt-2 flex justify-end">
          <Button
            variant="secondary"
            size="sm"
            disabled={saving || notes === (enquiry.notes ?? '')}
            onClick={() => onUpdate({ notes })}
          >
            Save notes
          </Button>
        </div>
      </section>

      <section className="rounded-xl bg-gradient-to-br from-violet-50 to-indigo-50 p-4 dark:from-violet-900/20 dark:to-indigo-900/20">
        <p className="mb-3 text-sm text-neutral-700 dark:text-neutral-300">
          Ready to start? This opens the new business form with these details filled in.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button variant="primary" onClick={onCreateBusiness}>
            Create business
          </Button>
          {enquiry.converted_tenant_id && (
            <Button variant="ghost" onClick={() => onOpenBusiness(enquiry.converted_tenant_id!)}>
              Open the business
            </Button>
          )}
        </div>
      </section>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
        <Field label="Name">{enquiry.name}</Field>
        <Field label="Business name">{enquiry.business_name}</Field>
        <Field label="Email">
          <a
            href={`mailto:${enquiry.contact_email}`}
            className="text-brand-600 hover:underline dark:text-brand-400"
          >
            {enquiry.contact_email}
          </a>
        </Field>
        <Field label="Phone">
          <a href={`tel:${enquiry.phone}`} className="font-mono tabular-nums text-brand-600 hover:underline dark:text-brand-400">
            {enquiry.phone}
          </a>
        </Field>
        <Field label="Business type">
          {enquiry.business_type && BUSINESS_TYPE_LABELS[enquiry.business_type]}
        </Field>
        <Field label="City">{enquiry.city}</Field>
        <Field label="Reason">{REASON_LABELS[enquiry.interest]}</Field>
        <Field label="Plan">{enquiry.plan_name}</Field>
        <div className="col-span-2">
          <Field label="Message">
            {enquiry.message && <span className="whitespace-pre-wrap">{enquiry.message}</span>}
          </Field>
        </div>
        <div className="col-span-2">
          <Field label="Sent from page">{enquiry.source_page}</Field>
        </div>
        <Field label="Sent on">{formatWhen(enquiry.created_at)}</Field>
        <Field label="Agreed to be contacted on">{formatWhen(enquiry.consent_at)}</Field>
        <Field label="Status">
          <StatusPill status={enquiry.status} />
        </Field>
        <Field label="First contacted on">{formatWhen(enquiry.contacted_at)}</Field>
        <Field label="Email to team">
          <NotifyPill status={enquiry.notify_status} />
        </Field>
        <Field label="Last changed on">{formatWhen(enquiry.updated_at)}</Field>
        <div className="col-span-2">
          <Field label="Last changed by">
            {enquiry.handled_by && <span className="font-mono text-xs">{enquiry.handled_by}</span>}
          </Field>
        </div>
        <div className="col-span-2">
          <Field label="Business made from it">
            {enquiry.converted_tenant_id && (
              <span className="font-mono text-xs">{enquiry.converted_tenant_id}</span>
            )}
          </Field>
        </div>
        <Field label="IP address">
          {enquiry.ip && <span className="font-mono text-xs">{enquiry.ip}</span>}
        </Field>
        <div className="col-span-2">
          <Field label="Browser">
            {enquiry.user_agent && <span className="text-xs">{enquiry.user_agent}</span>}
          </Field>
        </div>
      </dl>
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

function EnquiriesContent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [filters, setFilters] = useState<EnquiryFilters>({});
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);

  function applyFilters(next: EnquiryFilters) {
    setFilters(next);
    setPage(1);
  }

  // Search waits until typing stops.
  useEffect(() => {
    const value = searchInput.trim();
    if (value === (filters.q ?? '')) return;
    const timer = setTimeout(() => applyFilters({ ...filters, q: value || undefined }), 300);
    return () => clearTimeout(timer);
  }, [searchInput, filters]);

  const summaryQuery = useQuery({
    queryKey: [...LIST_KEY, 'summary'],
    queryFn: () => enquiriesService.summary(),
  });

  const listQuery = useQuery({
    queryKey: [...LIST_KEY, 'list', filters, page],
    queryFn: () => enquiriesService.list(filters, page, PAGE_SIZE),
  });

  // Same cache entry as the new-business form.
  const { data: plans } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn: () => subscriptionService.listPlans(),
    staleTime: 5 * 60 * 1000,
  });

  const rows = listQuery.data?.data ?? [];

  // Its own query, so the panel stays open when a filter drops the row from the list.
  const { data: selected } = useQuery({
    queryKey: ['platform-enquiry', selectedId],
    queryFn: () => enquiriesService.get(selectedId!),
    enabled: selectedId !== null,
    placeholderData: rows.find((r) => r.id === selectedId),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateEnquiryInput }) =>
      enquiriesService.update(id, input),
    onSuccess: (saved, { input }) => {
      queryClient.setQueryData(['platform-enquiry', saved.id], saved);
      queryClient.invalidateQueries({ queryKey: LIST_KEY });
      toast({
        variant: 'success',
        title: 'Saved',
        description: input.status ? `Marked ${STATUS_LABELS[input.status]}.` : 'Your notes are saved.',
      });
    },
    onError: (err: Error) => {
      toast({ variant: 'error', title: 'Could not save', description: err.message || 'Please try again.' });
    },
  });

  function openEnquiry(id: string) {
    setSelectedId(id);
    setPanelOpen(true);
  }

  function createBusiness(enquiry: Enquiry) {
    const state: TenantCreateRouterState = {
      prefill: toPrefill(enquiry, plans ?? []),
      enquiryId: enquiry.id,
    };
    navigate('/tenants/new', { state });
  }

  const summary = summaryQuery.data;
  const meta = listQuery.data?.meta;
  const totalPages = meta ? Math.max(1, Math.ceil(meta.total / meta.limit)) : 1;
  const filtered = Object.values(filters).some(Boolean);

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <div className="relative mb-6 overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-violet-600 to-pink-600 p-6 text-white shadow-lg">
        <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-white/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-10 -left-10 h-40 w-40 rounded-full bg-white/5 blur-3xl" />
        <div className="relative">
          <div className="mb-1 text-[10px] uppercase tracking-[0.18em] text-indigo-100">
            Platform · Website
          </div>
          <h1 className="text-2xl font-bold text-white">Enquiries</h1>
          <p className="mt-1 max-w-2xl text-sm text-indigo-100">
            These are the messages people send from the Orio360 website. Call or write back, mark
            each one, and make a business when they are ready.
          </p>
        </div>
      </div>

      {/* KPI cards */}
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {KPI_CARDS.map((card) => (
          <div
            key={card.status}
            data-testid={`enquiry-kpi-${card.status}`}
            className={cn(
              'rounded-xl border border-neutral-100 p-5 shadow-md transition-all duration-200 hover:shadow-lg dark:border-neutral-700',
              card.tint,
            )}
          >
            <div className="mb-3 flex items-center gap-3">
              <div
                className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br text-white',
                  card.gradient,
                )}
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d={card.icon} />
                </svg>
              </div>
              <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
                {STATUS_LABELS[card.status]}
              </span>
            </div>
            <p className="text-2xl font-bold tabular-nums text-neutral-900 dark:text-white">
              {summary ? summary.by_status[card.status] : '—'}
            </p>
            <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
              {card.status === 'NEW'
                ? `${summary ? summary.new_last_7_days : '—'} in the last 7 days`
                : card.hint}
            </p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="mb-6 rounded-2xl bg-white/80 p-4 shadow-md backdrop-blur-sm dark:bg-neutral-900/80">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-full sm:w-72">
            <Input
              variant="search"
              aria-label="Search"
              placeholder="Search name, business, email or phone"
              value={searchInput}
              onChange={setSearchInput}
            />
          </div>
          <Select
            id="enquiry-status"
            label="Status"
            options={STATUS_OPTIONS}
            value={filters.status ?? ''}
            onChange={(v) => applyFilters({ ...filters, status: firstValue(v) as EnquiryStatus | undefined })}
            placeholder="All statuses"
            className="w-40"
          />
          <Select
            id="enquiry-reason"
            label="Reason"
            options={REASON_OPTIONS}
            value={filters.interest ?? ''}
            onChange={(v) => applyFilters({ ...filters, interest: firstValue(v) as EnquiryInterest | undefined })}
            placeholder="All reasons"
            className="w-44"
          />
          <Select
            id="enquiry-business-type"
            label="Business type"
            options={BUSINESS_TYPE_OPTIONS}
            value={filters.business_type ?? ''}
            onChange={(v) =>
              applyFilters({ ...filters, business_type: firstValue(v) as EnquiryBusinessType | undefined })
            }
            placeholder="All types"
            className="w-40"
          />
          <Input
            id="enquiry-from"
            type="date"
            label="From"
            value={filters.from ?? ''}
            onChange={(v) => applyFilters({ ...filters, from: v || undefined })}
            className="w-40"
          />
          <Input
            id="enquiry-to"
            type="date"
            label="To"
            value={filters.to ?? ''}
            onChange={(v) => applyFilters({ ...filters, to: v || undefined })}
            className="w-40"
          />
          {(filtered || searchInput) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchInput('');
                applyFilters({});
              }}
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      {listQuery.isError ? (
        <Alert
          variant="danger"
          title="Could not load the enquiries"
          description="Something went wrong. Please try again."
          action={{ label: 'Try again', onClick: () => listQuery.refetch() }}
        />
      ) : listQuery.isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={48} />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-neutral-200 p-10 text-center dark:border-neutral-700">
          <p className="text-base font-semibold text-neutral-900 dark:text-white">
            {filtered ? 'No enquiries match' : 'No enquiries yet'}
          </p>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            {filtered
              ? 'Try other filters, or press Clear.'
              : 'When someone sends the form on the website, it shows here.'}
          </p>
        </div>
      ) : (
        <div className="animate-slide-up">
          <div className="overflow-x-auto rounded-2xl border border-neutral-100 bg-white shadow-md dark:border-neutral-700 dark:bg-neutral-900">
            <table className="min-w-full text-sm">
              <thead className="bg-gradient-to-r from-indigo-50 to-violet-50 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:from-neutral-800 dark:to-neutral-800 dark:text-neutral-400">
                <tr>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Business</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Reason</th>
                  <th className="px-4 py-3">Plan</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Email to team</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => openEnquiry(row.id)}
                    className="cursor-pointer border-t border-neutral-100 transition-all duration-200 even:bg-neutral-50 hover:bg-brand-50 dark:border-neutral-800 dark:even:bg-neutral-800/40 dark:hover:bg-brand-900/20"
                  >
                    <td className="whitespace-nowrap px-4 py-3">
                      <p className="tabular-nums text-neutral-900 dark:text-neutral-100">
                        {formatDay(row.created_at)}
                      </p>
                      <p className="text-xs tabular-nums text-neutral-400 dark:text-neutral-500">
                        {formatTime(row.created_at)}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      {/* The click reaches the row; the button gives keyboard users a way in. */}
                      <button
                        type="button"
                        className="text-left font-semibold text-neutral-900 hover:text-brand-600 dark:text-white dark:hover:text-brand-400"
                      >
                        {row.name}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-neutral-700 dark:text-neutral-300">
                      {row.business_name ?? '—'}
                      {row.business_type && (
                        <p className="text-xs text-neutral-400 dark:text-neutral-500">
                          {BUSINESS_TYPE_LABELS[row.business_type]}
                        </p>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 font-mono text-xs tabular-nums text-neutral-700 dark:text-neutral-300">
                      {row.phone}
                    </td>
                    <td className="px-4 py-3 text-neutral-700 dark:text-neutral-300">
                      {REASON_LABELS[row.interest]}
                    </td>
                    <td className="px-4 py-3 text-neutral-700 dark:text-neutral-300">
                      {row.plan_name ?? '—'}
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill status={row.status} />
                    </td>
                    <td className="px-4 py-3">
                      <NotifyPill status={row.notify_status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between text-sm text-neutral-500 dark:text-neutral-400">
              <span className="tabular-nums">
                Page {page} of {totalPages} ({meta?.total ?? 0} in all)
              </span>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  Previous
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <Drawer
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        title={selected?.name ?? 'Enquiry'}
        width="480px"
      >
        {selected && (
          <EnquiryPanel
            key={selected.id}
            enquiry={selected}
            saving={updateMutation.isPending}
            onUpdate={(input) => updateMutation.mutate({ id: selected.id, input })}
            onCreateBusiness={() => createBusiness(selected)}
            onOpenBusiness={(tenantId) => navigate(`/tenants/${tenantId}`)}
          />
        )}
      </Drawer>
    </div>
  );
}

export function EnquiriesPage() {
  useEffect(() => {
    document.title = 'Enquiries — Orio360 Admin';
  }, []);

  return (
    <ErrorBoundary>
      <PageContainer>
        <EnquiriesContent />
      </PageContainer>
    </ErrorBoundary>
  );
}
